import { Trans } from '@lingui/react';
import { Checkbox } from '@startuptools/ui/Checkbox';
import { Box } from '@startuptools/ui/Box';
import { Button } from '@startuptools/ui/Button';
import { Stack } from '@startuptools/ui/Stack';
import { Typography } from '@startuptools/ui/Typography';
import { FormControl } from '@startuptools/ui/FormControl';
import { FormLabel } from '@startuptools/ui/FormLabel';
import { Input } from '@startuptools/ui/Input';
import { useCompanyId } from '@startuptools/common/react-wrapper';
import { useSetMeetingPostalVoteMutation } from '../../../../graphql/react-operations';
import { Controller, type SubmitHandler, useForm } from 'react-hook-form';
import { type GqlMeeting } from '../../../../graphql/base-types.graphql';
import { DateTime } from 'luxon';
import { DateInput } from '@startuptools/ui/custom/DateInput';
import { ErrorDisplay } from '../../../../components/error-display/ErrorDisplay';
import { useStepper } from '../../../../components/steppers/common/StepperContext';
import { useStepperStepContent } from '../../../../components/steppers/common/StepperStepContentContext';
import { FormHelperText } from '@startuptools/ui/FormHelperText';

type FormValues = Pick<GqlMeeting, 'postalVote' | 'postalVoteEmail'> & { postalVoteDate: DateTime | null };

export const GMStepPostalVoting = ({
  meeting,
}: {
  meeting: Pick<GqlMeeting, 'id' | 'postalVote' | 'postalVoteDate' | 'postalVoteEmail'>;
}) => {
  const companyId = useCompanyId();
  const { stepNext } = useStepper();
  const { validatorStatus } = useStepperStepContent();

  // eslint-disable-next-line sut/loading-mutation
  const [doSetPostalVote] = useSetMeetingPostalVoteMutation();

  const { control, register, handleSubmit, watch } = useForm<FormValues>({
    defaultValues: {
      postalVote: meeting.postalVote,
      postalVoteDate: meeting.postalVoteDate ? DateTime.fromISO(meeting.postalVoteDate) : null,
      postalVoteEmail: meeting.postalVoteEmail ?? null,
    },
  });

  const postalVote = watch('postalVote');

  const onSubmit: SubmitHandler<FormValues> = async ({ postalVoteDate, ...values }) => {
    await doSetPostalVote({
      variables: {
        companyId,
        input: {
          id: meeting.id,
          postalVoteDate: postalVoteDate?.toUTC(0, { keepLocalTime: true }).toISO() ?? null,
          ...values,
        },
      },
    });
    return stepNext();
  };

  return (
    <form onSubmit={handleSubmit(onSubmit)}>
      <Stack gap={3}>
        <Typography level="h4">
          <Trans id="Postal Voting" />
        </Typography>

        <Controller
          control={control}
          name="postalVote"
          render={({ field: { value, onChange, ...field } }) => (
            <Checkbox
              label={<Trans id="Allow postal voting" />}
              checked={value}
              onChange={ev => onChange(ev.target.checked)}
              {...field}
            />
          )}
        />

        {postalVote && (
          <>
            <FormControl required sx={{ maxWidth: 300 }}>
              <FormLabel>
                <Trans id="Email for postal voting" />
              </FormLabel>
              <Input {...register('postalVoteEmail')} type="email" />
            </FormControl>

            <Controller
              control={control}
              name="postalVoteDate"
              rules={{ required: true }}
              render={({ field, fieldState }) => (
                <FormControl
                  sx={{ maxWidth: 300 }}
                  error={Boolean(fieldState.error)}
                  color={fieldState.invalid ? 'danger' : undefined}
                >
                  <FormLabel>
                    <Trans id="Last day to vote" />
                    {' *'}
                  </FormLabel>
                  <DateInput {...field} />
                  {fieldState.error?.type === 'required' && (
                    <FormHelperText color="danger">
                      <Trans id="Please set a date" />
                    </FormHelperText>
                  )}
                </FormControl>
              )}
            />
          </>
        )}

        {validatorStatus?.hasError && <ErrorDisplay error={validatorStatus.error} />}

        <Box>
          <Button type="submit">
            <Trans id="Save and Continue" />
          </Button>
        </Box>
      </Stack>
    </form>
  );
};
